import React from 'react'
import classnames from 'classnames'
import type {
    ReconciliationViewPreset,
    ReconciliationIssueType,
    ReconciliationSummaryVO,
} from '../../../apis/types'

interface ReconciliationFiltersProps {
    activeView: ReconciliationViewPreset
    onViewChange: (view: ReconciliationViewPreset) => void
    activeIssueType: ReconciliationIssueType | undefined
    activeIssueLabel: string | undefined
    onIssueTypeChange: (issueType: ReconciliationIssueType | undefined) => void
    summary: ReconciliationSummaryVO | null
    filteredCount: number
}

const ReconciliationFilters: React.FC<ReconciliationFiltersProps> = ({
    activeView,
    onViewChange,
    activeIssueType,
    activeIssueLabel,
    onIssueTypeChange,
    summary,
    filteredCount,
}) => {
    const total = summary?.totalRows ?? 0
    const issueCount = summary?.rowsWithIssues ?? 0
    const cleanCount = summary?.fullyReconciledRows ?? 0
    const mismatchCount = summary?.statusCounts?.mismatch ?? 0

    const tabs: Array<{ key: ReconciliationViewPreset; label: string; count: number }> = [
        { key: 'all', label: 'All accounts', count: total },
        { key: 'issuesOnly', label: 'Issues only', count: issueCount },
        { key: 'fullyReconciled', label: 'Fully reconciled', count: cleanCount },
        { key: 'mismatches', label: 'Mismatches', count: mismatchCount },
    ]

    const hasActiveIssueFilter = activeIssueType != null

    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Show:</span>
            {tabs.map((tab) => (
                <button
                    key={tab.key}
                    onClick={() => {
                        onViewChange(tab.key)
                        onIssueTypeChange(undefined)
                    }}
                    className={classnames(
                        'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                        activeView === tab.key && !hasActiveIssueFilter
                            ? 'bg-indigo-100 dark:bg-indigo-400/20 text-indigo-700 dark:text-indigo-300'
                            : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                    )}
                >
                    {tab.label} ({tab.count})
                </button>
            ))}

            {hasActiveIssueFilter && (
                <>
                    <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                        Filtered: {activeIssueLabel ?? `showing ${filteredCount} accounts`}
                    </span>
                    <button
                        onClick={() => {
                            onIssueTypeChange(undefined)
                            onViewChange('all')
                        }}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium cursor-pointer"
                    >
                        &times; Clear filter
                    </button>
                </>
            )}
        </div>
    )
}

export default ReconciliationFilters
